import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, Clock, Award, Edit, Plus, HelpCircle, Image as ImageIcon } from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';

const TestQuestions = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getTestQuestions } = useFirebase();
  
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Get URL parameters
  const contentId = searchParams.get('contentId');
  const testName = searchParams.get('testName');

  useEffect(() => {
    if (testId && contentId) {
      loadTestDetails();
    }
  }, [testId, contentId]);

  const loadTestDetails = async () => {
    try {
      setLoading(true);
      
      // Load all tests for the content
      const allTests = await getTestQuestions(contentId);
      
      // Find the specific test
      const foundTest = allTests.find(t => 
        t.id === testId || 
        (t.testName || t.title) === decodeURIComponent(testName || '')
      );
      
      if (foundTest) {
        setTest(foundTest);
      } else {
        console.error('Test not found');
      }
    } catch (error) {
      console.error('Error loading test details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test details...</p>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Test Not Found</h2>
          <p className="text-gray-600 mb-4">The requested test could not be found.</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-purple-500 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-8 h-8" />
                  <h1 className="text-2xl font-bold">Test Questions</h1>
                </div>
                <p className="text-purple-100">
                  Viewing: <span className="font-medium">{test.testName || test.title}</span>
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/add-test/${contentId}?edit=${encodeURIComponent(test.testName || test.title)}`)}
                className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-purple-50 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit Test
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Test Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Test Name</p>
                  <p className="font-semibold text-gray-900">{test.testName || test.title}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <HelpCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Questions</p>
                  <p className="font-semibold text-blue-600">{test.questions?.length || 0}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Award className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Marks</p>
                  <p className="font-semibold text-green-600">{(test.questions?.length || 0) * 4}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Duration</p>
                  <p className="font-semibold text-orange-600">
                    {test.startDate && test.endDate ? 
                      `${test.startDate} - ${test.endDate}` : 
                      'Not set'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Questions List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Questions ({test.questions?.length || 0})
              </h2>
              <button
                onClick={() => navigate(`/add-test/${contentId}?edit=${encodeURIComponent(test.testName || test.title)}`)}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Questions
              </button>
            </div>
          </div>

          <div className="p-6">
            {test.questions && test.questions.length > 0 ? (
              <div className="space-y-6">
                {test.questions.map((question, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <span className="bg-purple-100 text-purple-600 text-sm font-medium px-2 py-1 rounded">
                        Q{index + 1}
                      </span>
                      <div className="flex-1">
                        {/* Question Type Badge */}
                        {question.questionType === 'image' && (
                          <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded mb-2">
                            <ImageIcon className="w-3 h-3" />
                            Image Question
                          </div>
                        )}
                        
                        {/* Question Image Display */}
                        {(question.questionImageURL || question.questionImage) && (
                          <div className="mb-4">
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Question Image:
                              </div>
                              <img
                                src={question.questionImageURL || question.questionImage}
                                alt={`Question ${index + 1} image`}
                                className="max-w-full max-h-96 object-contain rounded border border-gray-300 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => window.open(question.questionImageURL || question.questionImage, '_blank')}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <div className="text-red-500 text-sm mt-2 hidden">
                                ⚠️ Failed to load image. The image may have been deleted or the URL is invalid.
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <p className="text-gray-900 font-medium mb-3">{question.question}</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                          {question.options?.map((option, optIndex) => (
                            <div 
                              key={optIndex}
                              className={`p-2 rounded border ${
                                optIndex === question.correctAnswer 
                                  ? 'bg-green-50 border-green-200 text-green-800' 
                                  : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <span className="font-medium">
                                {String.fromCharCode(65 + optIndex)}. 
                              </span>
                              {option}
                              {optIndex === question.correctAnswer && (
                                <span className="ml-2 text-green-600 text-sm font-medium">✓ Correct</span>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {question.explanation && (
                          <div className="bg-blue-50 border border-blue-200 rounded p-3">
                            <p className="text-sm text-blue-800">
                              <strong>Explanation:</strong> {question.explanation}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <HelpCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Questions Yet</h3>
                <p className="text-gray-600 mb-4">This test doesn't have any questions yet.</p>
                <button
                  onClick={() => navigate(`/add-test/${contentId}?edit=${encodeURIComponent(test.testName || test.title)}`)}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add First Question
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestQuestions;